import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Avatar,
  Chip,
  TextField,
  InputAdornment,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Delete,
  Block,
  Search,
  CheckCircle
} from '@mui/icons-material';
import { deleteUser, updateUserStatus } from '../../services/adminService';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const loadUsers = async () => {
    try {
      // Önce seviye bilgilerini al
      const levelsRef = collection(db, 'levels');
      const levelsSnapshot = await getDocs(levelsRef);
      const levels = levelsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Kullanıcıları getir
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      
      // Her kullanıcı için seviyeyi hesapla
      const usersData = snapshot.docs.map(doc => {
        const userData = doc.data();
        // Seviyeyi puanlara göre hesapla
        let userLevel = 1;
        for (const level of levels) {
          if (userData.score >= level.requiredScore) {
            userLevel = parseInt(level.name.split(' ')[1]);
          } else {
            break;
          }
        }
        return {
          id: doc.id,
          ...userData,
          level: userLevel
        };
      });

      setUsers(usersData);
    } catch (error) {
      console.error('Kullanıcılar yüklenirken hata:', error);
      showSnackbar('Kullanıcılar yüklenirken hata oluştu', 'error');
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Bu kullanıcıyı silmek istediğinizden emin misiniz?')) {
      try {
        await deleteUser(userId);
        loadUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      await updateUserStatus(userId, !currentStatus);
      loadUsers();
    } catch (error) {
      console.error('Error updating user status:', error);
    }
  };

  const filteredUsers = users.filter(user => 
    user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box>
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Kullanıcı ara..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
      />

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Kullanıcı</TableCell>
              <TableCell>E-posta</TableCell>
              <TableCell>Seviye</TableCell>
              <TableCell>Puan</TableCell>
              <TableCell>Durum</TableCell>
              <TableCell>İşlemler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((user) => (
              <TableRow key={user.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      {user.username.charAt(0).toUpperCase()}
                    </Avatar>
                    {user.username}
                  </Box>
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Chip 
                    label={`Seviye ${user.level}`}
                    color="primary"
                    size="small"
                  />
                </TableCell>
                <TableCell>{user.score}</TableCell>
                <TableCell>
                  <Chip 
                    label={user.isActive ? "Aktif" : "Engelli"}
                    color={user.isActive ? "success" : "error"}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Tooltip title={user.isActive ? "Kullanıcıyı Engelle" : "Engeli Kaldır"}>
                    <IconButton 
                      onClick={() => handleToggleStatus(user.id, user.isActive)}
                      color={user.isActive ? "error" : "success"}
                    >
                      {user.isActive ? <Block /> : <CheckCircle />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Kullanıcıyı Sil">
                    <IconButton 
                      onClick={() => handleDeleteUser(user.id)}
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

export default UserManagement; 